import { toast } from 'sonner';
import { z } from 'zod';
import { create } from 'zustand';

import {
  clearTokens,
  getTokens,
  isAuthenticated,
  refreshAccessToken,
  setTokens,
} from '../api/auth';
import { apiGet, apiPatch, apiPost } from '../api/client';
import { API_CONFIG } from '../api/config';
import {
  type LoginResponse,
  type LoginSendCodeRequest,
  type LoginVerifyCodeRequest,
  type User,
  LoginResponseSchema,
} from '../schemas/auth.schema';
import { handleAuthExpiry } from '../utils/authExpiry';

export interface Permissions {
  // Members management
  canCreateNewMembers: boolean;
  canUpdateMembersDetails: boolean;
  canDeleteMembers: boolean;
  canTableListMembers: boolean;
  canGridListMembers: boolean;
  canFilterMembers: boolean;
  canViewInactiveMembers: boolean;

  // Debtors management
  canCreateDebtors: boolean;
  canViewInactiveDebtors: boolean;
  canDeleteDebtors: boolean;
  canFilterDebtors: boolean;
  canViewDebtorEntryCreator: boolean;

  // Accounts management
  canCreateAccountsEntry: boolean;
  canApproveAccountsEntry: boolean;
  canListPendingAccountsEntry: boolean;
  canFilterAccountsEntry: boolean;
}

export interface AuthUser extends Omit<User, 'profile'> {
  profile: {
    address: string;
    photo?: string | null;
    date_of_birth?: string | null;
    membership_id: string | null;
    finance_name: string;
  };
  permissions: Permissions;
}

// Auth store state
interface AuthState {
  isAuthenticated: boolean;
  isLoggedInNow: boolean;
  user: AuthUser | null;
  isLoading: boolean;
  otpSent: boolean;
  error: string | null;

  // Actions
  setIsLoggedInNow: (isLoggedIn: boolean) => void;
  sendOtp: (phoneNumber: string) => Promise<boolean>;
  verifyOtp: (phoneNumber: string, otp: string) => Promise<boolean>;
  logout: () => void;
  checkAuthStatus: () => Promise<boolean>;
  updateCurrentUser: (
    updates: FormData | Partial<AuthUser>,
  ) => Promise<boolean>;
  initializeCurrentUser: () => Promise<boolean>;

  // Additional UI state actions
  setOtpSent: (sent: boolean) => void;
}


const getRolePermissions = (role: string): Permissions => {
  const permissions: Permissions = {
    canCreateNewMembers: false,
    canUpdateMembersDetails: false,
    canDeleteMembers: false,
    canTableListMembers: false,
    canGridListMembers: false,
    canFilterMembers: false,
    canViewInactiveMembers: false,
    canCreateDebtors: false,
    canViewInactiveDebtors: false,
    canDeleteDebtors: false,
    canFilterDebtors: false,
    canViewDebtorEntryCreator: false,
    canCreateAccountsEntry: false,
    canApproveAccountsEntry: false,
    canListPendingAccountsEntry: false,
    canFilterAccountsEntry: false,
  };

  switch (role) {
    case 'admin':
      permissions.canCreateNewMembers = true;
      permissions.canUpdateMembersDetails = true;
      permissions.canDeleteMembers = true;
      permissions.canTableListMembers = true;
      permissions.canGridListMembers = false;
      permissions.canFilterMembers = true;
      permissions.canViewInactiveMembers = true;
      permissions.canCreateDebtors = true;
      permissions.canViewInactiveDebtors = true;
      permissions.canDeleteDebtors = true;
      permissions.canFilterDebtors = true;
      permissions.canViewDebtorEntryCreator = true;
      permissions.canCreateAccountsEntry = false;
      permissions.canApproveAccountsEntry = false;
      permissions.canListPendingAccountsEntry = true;
      permissions.canFilterAccountsEntry = true;
      break;

    case 'financier':
      permissions.canCreateDebtors = true;
      permissions.canGridListMembers = true;
      break;

    case 'president':
    case 'vice_president':
      permissions.canCreateDebtors = true;
      permissions.canGridListMembers = true;
      permissions.canApproveAccountsEntry = true;
      permissions.canListPendingAccountsEntry = true;
      break;

    case 'secretary':
    case 'assistant_secretary':
      permissions.canCreateDebtors = true;
      permissions.canGridListMembers = true;
      permissions.canApproveAccountsEntry = true;
      permissions.canListPendingAccountsEntry = true;
      break;

    case 'treasurer':
      permissions.canCreateDebtors = true;
      permissions.canGridListMembers = true;
      permissions.canCreateAccountsEntry = true;
      permissions.canListPendingAccountsEntry = true;
      break;

    case 'assistant_treasurer':
      permissions.canCreateDebtors = true;
      permissions.canGridListMembers = true;
      permissions.canCreateAccountsEntry = true;
      permissions.canListPendingAccountsEntry = true;
      break;
  }

  return permissions;
};

// Create auth store
export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: isAuthenticated(),
  isLoggedInNow: false,
  user: null,
  isLoading: false,
  otpSent: false,
  error: null,

  setIsLoggedInNow: async (isLoggedIn) => {
    set({ isLoggedInNow: isLoggedIn });
  },

  /**
   * Send OTP code to phone number (via WhatsApp)
   */
  sendOtp: async (phoneNumber) => {
    try {
      set({ isLoading: true, error: null });

      const response = await apiPost<{ detail: string }, LoginSendCodeRequest>(
        API_CONFIG.ENDPOINTS.AUTH.LOGIN_SEND_CODE,
        { username: phoneNumber },
      );

      if (!response.success) {
        set({
          isLoading: false,
          error: response.error?.message || 'Failed to send OTP',
        });
        toast.error(response.error?.message || 'Failed to send OTP');
        return false;
      }

      set({ otpSent: true, isLoading: false, error: null });
      toast.success('OTP sent to your WhatsApp');
      return true;
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to send OTP',
      });
      toast.error(
        error instanceof Error ? error.message : 'Failed to send OTP',
      );
      return false;
    }
  },

  /**
   * Verify OTP code
   */
  verifyOtp: async (phoneNumber, otp) => {
    try {
      set({ isLoading: true, error: null });

      const response = await apiPost<LoginResponse, LoginVerifyCodeRequest>(
        API_CONFIG.ENDPOINTS.AUTH.LOGIN_VERIFY_CODE,
        { username: phoneNumber, code: otp },
      );

      if (!response.success || !response.data) {
        set({
          isLoading: false,
          error: response.error?.message || 'Invalid OTP',
        });
        toast.error(response.error?.message || 'Invalid OTP');
        return false;
      }

      // Validate response with Zod
      const validationResult = LoginResponseSchema.safeParse(response.data);
      if (!validationResult.success) {
        set({
          isLoading: false,
          error: 'Invalid response format',
        });
        toast.error('Server returned invalid data format');
        return false;
      }

      const { user, access, refresh, access_expiration, refresh_expiration } =
        validationResult.data;

      // Store tokens
      setTokens(
        access,
        access_expiration.toISOString(),
        refresh,
        refresh_expiration.toISOString(),
      );


      // Transform user data
      const authUser: AuthUser = {
        id: user.id,
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name,
        date_joined: user.date_joined,
        role: user.role,
        profile: user.profile,
        permissions: getRolePermissions(user.role),
      };

      set({
        isAuthenticated: true,
        isLoggedInNow: true,
        user: authUser,
        isLoading: false,
        otpSent: false,
        error: null,
      });

      // Set flag to indicate this is a fresh login (not auto-redirect)
      sessionStorage.setItem('loginRedirect', 'true');
      
      toast.success('Login successful');
      return true;
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Invalid OTP',
      });
      toast.error(error instanceof Error ? error.message : 'Invalid OTP');
      return false;
    }
  },

  /**
   * Logout
   */
  logout: () => {
    // Clear all tokens and user data
    clearTokens();
    
    // Clear any additional user data from localStorage
    const userDataKeys = [
      // Add any user-specific localStorage keys here
    ];
    userDataKeys.forEach(key => localStorage.removeItem(key));
    
    // Clear sessionStorage as well
    sessionStorage.clear();
    
    // Reset all auth state
    set({
      isAuthenticated: false,
      isLoggedInNow: false,
      user: null,
      isLoading: false,
      otpSent: false,
      error: null,
    });
    
    toast.info('You have been logged out');
  },

  /**
   * Check authentication status and refresh token if needed
   */
  checkAuthStatus: async () => {
    // Get tokens
    const { accessToken, accessExpiry, refreshToken, refreshExpiry } =
      getTokens();

    // Case 1: No tokens exist
    if (!accessToken && !refreshToken) {
      set({ isAuthenticated: false, user: null });
      return false;
    }

    // Case 2: Access token is valid
    if (accessToken && accessExpiry) {
      const expiryDate = new Date(accessExpiry);
      const now = new Date();
      if (expiryDate > now) {
        set({ isAuthenticated: true });
        return true;
      }
    }

    // Case 3: Access token expired but refresh token valid
    if (refreshToken && refreshExpiry) {
      const refreshExpiryDate = new Date(refreshExpiry);
      const now = new Date();

      if (refreshExpiryDate > now) {
        // Try to refresh the token
        try {
          await refreshAccessToken();
          set({ isAuthenticated: true });
          return true;
        } catch (error) {
          // If refresh fails, clear tokens and handle auth expiry
          clearTokens();
          set({ isAuthenticated: false, user: null });
          handleAuthExpiry('check-auth-status-refresh', error instanceof Error ? error : undefined);
          return false;
        }
      }
    }

    // Case 4: All tokens expired or invalid
    clearTokens();
    set({ isAuthenticated: false, user: null });
    return false;
  },

  /**
   * Update the current user's profile
   */
  updateCurrentUser: async (
    updates: FormData | Partial<AuthUser>,
  ): Promise<boolean> => {
    try {
      set({ isLoading: true, error: null });

      // Check if updates is already a FormData object
      const isFormData = updates instanceof FormData;

      if (isFormData) {
        // Direct FormData submission (for file uploads)
        const result = await apiPatch(API_CONFIG.ENDPOINTS.USER.ME, updates, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (!result.success) {
          throw new Error(result.error?.message || 'Failed to update profile');
        }
      } else {
        // Handle regular JSON updates (no file upload)
        const updateData: Record<string, any> = {};

        // Add basic fields
        if (updates.first_name !== undefined) {
          updateData.first_name = updates.first_name;
        }
        if (updates.last_name !== undefined) {
          updateData.last_name = updates.last_name;
        }

        // Add profile updates if they exist
        if (updates.profile) {
          const profileUpdates: Record<string, any> = {};

          if (updates.profile.address !== undefined) {
            profileUpdates.address = updates.profile.address;
          }
          if (updates.profile.date_of_birth !== undefined) {
            profileUpdates.date_of_birth = updates.profile.date_of_birth;
          }

          if (Object.keys(profileUpdates).length > 0) {
            updateData.profile = profileUpdates;
          }
        }

        // Make the API call
        const result = await apiPatch(API_CONFIG.ENDPOINTS.USER.ME, updateData);

        if (!result.success) {
          throw new Error(result.error?.message || 'Failed to update profile');
        }
      }

      // After successful update, refresh the user data
      await get().initializeCurrentUser();

      set({ isLoading: false });
      toast.success('Profile updated successfully');
      return true;
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : 'Failed to update profile',
      });
      toast.error(
        error instanceof Error ? error.message : 'Failed to update profile',
      );
      return false;
    }
  },

  initializeCurrentUser: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await apiGet(API_CONFIG.ENDPOINTS.USER.ME);

      if (!response.success || !response.data) {
        set({
          isLoading: false,
          error: response.error?.message || 'Failed to fetch user details',
        });
        
        // If it's a 401 error, handle auth expiry
        if (response.error?.code === 401) {
          handleAuthExpiry('initialize-current-user');
          return false;
        }
        
        toast.error(response.error?.message || 'Failed to fetch user details');
        return false;
      }

      // Define schema for user API response
      const UserResponseSchema = z
        .object({
          id: z.number(),
          username: z.string(),
          first_name: z.string(),
          last_name: z.string().optional(),
          date_joined: z.string().transform((val) => new Date(val)),
          role: z.string(),
          profile: z
            .object({
              address: z.string(),
              photo: z.string().url().nullish(),
              date_of_birth: z.string().nullish(),
              membership_id: z.string().nullish(),
              finance_name: z.string(),
              created_at: z.string().optional(),
              updated_at: z.string().optional(),
            })
            .passthrough(),
        })
        .passthrough();

      const validationResult = UserResponseSchema.safeParse(response.data);
      if (!validationResult.success) {
        set({
          isLoading: false,
          error: 'Invalid user data format',
        });
        toast.error('Server returned invalid user data format');
        return false;
      }

      const userData = validationResult.data;

      // Transform user data
      const authUser: AuthUser = {
        id: userData.id,
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        date_joined: userData.date_joined,
        role: userData.role,
        profile: {
          address: userData.profile.address,
          photo: userData.profile.photo,
          date_of_birth: userData.profile.date_of_birth,
          membership_id: userData.profile.membership_id,
          finance_name: userData.profile.finance_name,
        },
        permissions: getRolePermissions(userData.role),
      };

      set({
        isAuthenticated: true,
        user: authUser,
        isLoading: false,
        error: null,
      });

      return true;
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : 'Failed to initialize user',
      });
      toast.error(
        error instanceof Error ? error.message : 'Failed to initialize user',
      );
      return false;
    }
  },

  // Additional UI state actions
  setOtpSent: (sent: boolean) => {
    set({ otpSent: sent });
  },
}));
